import { ReportRow } from 'src/common/interfaces/report';
import { extractContainerIdsFromReportRows } from '../../../components/Grid/GridDataTransformer';
import { PermalinkRequestItem, PermalinkType } from '../../../http-clients/interfaces/types';

const PARTITION_SIZE = 100;

/**
 * Extracts container IDs from report rows and creates partitions for API requests
 *
 * @param rows - The report rows to extract container IDs from
 * @returns Array of partitioned request items, or null if no valid IDs found
 */
export const processContainerIds = (rows: ReportRow[] | undefined): PermalinkRequestItem[][] | null => {
    // Check if rows are valid
    if (!rows || !Array.isArray(rows)) {
        return null;
    }

    // Extract container IDs
    let containerIds: number[] = [];
    try {
        containerIds = extractContainerIdsFromReportRows(rows);
    } catch (error) {
        return null;
    }

    // Check if any container IDs were found
    if (!containerIds.length) {
        return null;
    }

    // Create partitions for API requests
    const partitions: PermalinkRequestItem[][] = [];
    for (let i = 0; i < containerIds.length; i += PARTITION_SIZE) {
        const partition = containerIds.slice(i, i + PARTITION_SIZE).map((id: number) => ({
            type: PermalinkType.CONTAINER,
            id,
        }));
        partitions.push(partition);
    }

    return partitions;
};

/**
 * Helper function to extract all unique container IDs from partitions.
 *
 * @param {PermalinkRequestItem[][]} partitions - The partitions containing container IDs.
 * @returns {Set<number>} - A set of all unique container IDs.
 */
const extractContainerIdsFromPartitions = (partitions: PermalinkRequestItem[][]): Set<number> => {
    const containerIds = new Set<number>();
    partitions.forEach((partition: PermalinkRequestItem[]) => {
        partition.forEach((item) => {
            containerIds.add(item.id);
        });
    });
    return containerIds;
};

/**
 * Helper function to handle container link failures by adding '-1' values for missing container IDs.
 * This will cause the UI to show the text instead of an infinite spinner.
 *
 * - When containerReportLinkMap is provided: merges successful container links with failed ones (marked as '-1')
 * - When containerReportLinkMap is not provided: creates a new map with '-1' values for all container IDs
 *
 * @param {PermalinkRequestItem[][]} partitions - The partitions containing all container IDs that were requested.
 * @param {Map<number, string>} [containerReportLinkMap] - Optional map containing successfully fetched container links.
 * @returns {Map<number, string>} - The map with '-1' values for missing/failed container IDs.
 */
export const handleContainerLinkFailures = (
    partitions: PermalinkRequestItem[][],
    containerReportLinkMap?: Map<number, string>
): Map<number, string> => {
    const resultMap = new Map<number, string>(containerReportLinkMap || []);
    const allContainerIds = extractContainerIdsFromPartitions(partitions);

    // Add '-1' for container IDs that weren't successfully fetched
    allContainerIds.forEach((containerId) => {
        if (!resultMap.has(containerId)) {
            resultMap.set(containerId, '-1');
        }
    });

    return resultMap;
};
